import React, { useEffect, useMemo, useRef, useState } from "react";
import { computeFEFOSplit, applyFEFOSplit, makeLineId } from "@/lib/fefoSplit";
import { AutoSplitFEFODialog } from "@/components/AutoSplitFEFODialog";
import { StockAlternativasDialog, ConfirmSalirDialog } from "@/components/StockAlternativasDialog";
import { DireccionEntregaSelect } from "@/components/ClienteContactos";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, shareApiPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, ClipboardList, AlertTriangle, ArrowLeft, Save, Layers } from "lucide-react";
import { ClienteCombobox } from "@/components/ClienteCombobox";
import {
  SalesLineRow, SalesLineCard, emptySalesLine, recalcSubtotal, applyProductoInfoToLine,
  calcResumenCaja, ResumenCajaBar,
} from "@/components/SalesLineRow";
import { ConsolidateOrdersDialog } from "@/components/ConsolidateOrdersDialog";

function emptyForm() {
  return {
    cliente_id: "",
    fecha: new Date().toISOString().slice(0, 10),
    observaciones: "",
    direccion_entrega: "", direccion_entrega_etiqueta: "",
    lineas: [],
  };
}

export default function SalesOrderForm() {
  const { id: editingId } = useParams();
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consolidateOpen, setConsolidateOpen] = useState(false);
  const [cotizIds, setCotizIds] = useState([]);  // cotizaciones consolidadas en esta NP
  const [precioDivergenteAviso, setPrecioDivergenteAviso] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [c, p, r] = await Promise.all([
          api.get("/masters/clientes"),
          api.get("/masters/productos-comerciales"),
          // ITER209: unidades comprometidas en otras notas de pedido pendientes
          api.get(`/sales/stock-reservado${editingId ? `?excluir_pedido_id=${editingId}` : ""}`)
            .then(x => x.data).catch(() => ({})),
        ]);
        if (!mounted) return;
        setClientes(c.data.filter(x => x.estado === "aprobado"));
        setProductos((p.data || []).map(prod => {
          const reservado = Number(r?.[prod.id] || 0);
          const bruto = Number(prod.stock_total ?? 0);
          return {
            ...prod,
            stock_bruto: bruto,
            stock_reservado: reservado,
            stock_total: Math.max(0, bruto - reservado),
          };
        }));

        if (editingId) {
          const { data: lista } = await api.get("/sales/pedidos");
          const it = lista.find(x => x.id === editingId);
          if (!it) {
            toast.error("Nota de pedido no encontrada");
            navigate("/pedidos", { replace: true });
            return;
          }
          if (it.estado && it.estado !== "pendiente") {
            toast.error(`No se puede editar un pedido en estado '${it.estado}'`);
            navigate("/pedidos", { replace: true });
            return;
          }
          setForm({
            cliente_id: it.cliente_id,
            fecha: it.fecha || new Date().toISOString().slice(0, 10),
            observaciones: it.observaciones || "",
            direccion_entrega: it.direccion_entrega || "",
            direccion_entrega_etiqueta: it.direccion_entrega_etiqueta || "",
            numero: it.numero || "",
            estado: it.estado || "",
            lineas: (it.lineas || []).map((l, i) => {
              const prod = p.data.find(x => x.id === l.producto_comercial_id);
              const unidadesPorCaja = l.unidades_por_caja || prod?.unidades_por_presentacion || 1;
              return {
                ...emptySalesLine(),
                ...l,
                _lineId: l._lineId || l.id || `l-edit-${i}`,
                descuento_pct: l.descuento_pct ?? (l.descuento && l.cantidad && l.precio_unitario ? +(100 * l.descuento / (l.cantidad * l.precio_unitario)).toFixed(2) : 0),
                _unidad_venta: l.unidad_venta || "unidad",
                _unidades_por_caja: unidadesPorCaja,
                _precio_unidad: prod?.precio_venta || 0,
                _precio_caja: (prod?.precio_venta || 0) * unidadesPorCaja,
                _pvp_unidad: prod?.pvp || 0,
                _pvp_caja: prod?.pvp_caja || (prod?.pvp || 0) * unidadesPorCaja,
                _precio_minimo_unidad: prod?.precio_minimo || 0,
                _precio_minimo_caja: prod?.precio_minimo_caja || 0,
                _costo_unidad: prod?.precio_costo || 0,
                _margen_aplicado: prod?.margen || 0,
                _margen_fuente: "producto",
                iva_porc: l.iva_porc ?? (prod?.iva_exento ? -1 : (prod?.iva_porcentaje ?? 0)),
                // ITER181: se hidratará abajo con datos frescos de /producto-info
                _lotes_disponibles: [],
              };
            }),
          });
          // ITER181 (Feb 2026): hidratar _lotes_disponibles y flags de corta fecha
          // de cada línea con datos frescos y excluyendo este pedido de la reserva.
          // Sin esto, detectFEFOSplit no puede sugerir división ni advertir de
          // sobrecarga del lote hasta que el usuario dispara "Guardar cambios".
          if (it.lineas && it.lineas.length > 0) {
            try {
              const params = new URLSearchParams();
              if (it.cliente_id) params.append("cliente_id", it.cliente_id);
              params.append("excluir_pedido_id", editingId);
              const qs = `?${params.toString()}`;
              const infos = await Promise.all(
                it.lineas.map(l => (
                  l.producto_comercial_id
                    ? api.get(`/sales/producto-info/${l.producto_comercial_id}${qs}`).then(r => r.data).catch(() => null)
                    : Promise.resolve(null)
                ))
              );
              if (mounted) {
                setForm(f => ({
                  ...f,
                  lineas: f.lineas.map((line, i) => {
                    const info = infos[i];
                    if (!info) return line;
                    return {
                      ...line,
                      _lotes_disponibles: info.lotes_disponibles || [],
                      _vencimiento_corto: !!(line.lote_id && (info.lotes_disponibles || []).find(x => x.id === line.lote_id)?.vencimiento_corto),
                      _dias_hasta_vencimiento: (info.lotes_disponibles || []).find(x => x.id === line.lote_id)?.dias_hasta_vencimiento ?? null,
                      _dias_alerta_venc_corto: info.dias_alerta_vencimiento_corto ?? 180,
                    };
                  }),
                }));
                // Post-hidratación: detectar split necesario en las líneas cargadas
                // (típicamente ocurre si el usuario venía editando pero refresco
                //  o si el stock real cambió por otro pedido concurrente).
                setTimeout(() => {
                  setForm(f => {
                    f.lineas.forEach((line, i) => {
                      const cant = parseFloat(line.cantidad) || 0;
                      if (cant > 0 && (line._lotes_disponibles || []).length > 0) {
                        detectFEFOSplit(i, line);
                      }
                    });
                    return f;
                  });
                }, 300);
              }
            } catch (_) { /* silencioso; el usuario aún puede editar */ }
          }
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [editingId, navigate]);

  const addLinea = () => setForm(f => ({ ...f, lineas: [...f.lineas, emptySalesLine()] }));
  const removeLinea = (idx) => setForm(f => ({ ...f, lineas: f.lineas.filter((_, i) => i !== idx) }));

  // ITER170: auto-split FEFO cuando la cantidad excede el stock del lote elegido
  const [splitProposal, setSplitProposal] = useState(null); // {idx, split, producto_nombre}
  const detectFEFOSplit = (idx, updatedLine) => {
    // ITER182: siempre proponer split si computeFEFOSplit devuelve plan
    // (aunque sea 1 lote alterno — el usuario debe ver la propuesta).
    if (!updatedLine.lote_id) return;
    const cant = Number(updatedLine.cantidad) || 0;
    const split = computeFEFOSplit(updatedLine, cant);
    if (split && split.plan.length >= 1) {
      setSplitProposal({ idx, split, producto_nombre: updatedLine.producto_nombre });
    }
  };
  const applyProposedSplit = () => {
    if (!splitProposal) return;
    setForm(f => ({
      ...f,
      lineas: applyFEFOSplit(f.lineas, splitProposal.idx, splitProposal.split, recalcSubtotal, makeLineId),
    }));
    setSplitProposal(null);
  };

  const updateLinea = (idx, field, value) => {
    setForm(f => {
      const newL = [...f.lineas];
      newL[idx] = { ...newL[idx], [field]: value };
      newL[idx] = recalcSubtotal(newL[idx]);
      // Al terminar de digitar la cantidad o cambiar unidad, detectar split FEFO
      // ITER180: incluir "unidad_venta" para re-evaluar cuando el usuario cambia
      // entre "caja" y "unidad" (cambia la métrica de disponibilidad).
      if (field === "cantidad" || field === "unidad_venta") {
        // Diferir a próximo tick para no bloquear el input
        setTimeout(() => detectFEFOSplit(idx, newL[idx]), 250);
      }
      return { ...f, lineas: newL };
    });
  };

  const onProductoChange = async (idx, producto_comercial_id) => {
    setForm(f => {
      const newL = [...f.lineas];
      newL[idx] = { ...newL[idx], producto_comercial_id, producto_nombre: "Cargando…" };
      return { ...f, lineas: newL };
    });
    try {
      const params = new URLSearchParams();
      if (form.cliente_id) params.append("cliente_id", form.cliente_id);
      if (editingId) params.append("excluir_pedido_id", editingId);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await api.get(`/sales/producto-info/${producto_comercial_id}${qs}`);
      setForm(f => {
        const newL = [...f.lineas];
        const l = newL[idx];
        if (l && l.producto_comercial_id === producto_comercial_id) {
          newL[idx] = applyProductoInfoToLine(l, data);
          const unidadDef = newL[idx]._unidad_venta;
          newL[idx].precio_unitario = unidadDef === "caja" ? (data.precio_caja || 0) : (data.precio_unidad || data.precio_venta || 0);
          newL[idx] = recalcSubtotal(newL[idx]);
        }
        return { ...f, lineas: newL };
      });
    } catch (err) { console.warn("producto-info load failed:", err); }
  };

  const onLoteChange = (idx, loteId) => setForm(f => {
    const newL = [...f.lineas];
    const lt = (newL[idx]._lotes_disponibles || []).find(x => x.id === loteId);
    if (lt) {
      newL[idx] = {
        ...newL[idx],
        lote_id: lt.id,
        numero_lote: lt.numero_lote || "",
        fecha_elaboracion: lt.fecha_elaboracion || "",
        fecha_vencimiento: lt.fecha_vencimiento || "",
        // ITER179: propagar flag de corta fecha al cambiar lote manualmente
        _vencimiento_corto: !!lt.vencimiento_corto,
        _dias_hasta_vencimiento: lt.dias_hasta_vencimiento ?? null,
      };
    }
    return { ...f, lineas: newL };
  });

  const toggleUnidadVenta = (idx, modo) => setForm(f => {
    const newL = [...f.lineas];
    newL[idx] = {
      ...newL[idx],
      _unidad_venta: modo,
      precio_unitario: modo === "caja" ? (newL[idx]._precio_caja || 0) : (newL[idx]._precio_unidad || 0),
    };
    newL[idx] = recalcSubtotal(newL[idx]);
    return { ...f, lineas: newL };
  });

  const totales = useMemo(() => {
    const subtotal = form.lineas.reduce((s, l) => s + (parseFloat(l.subtotal) || 0), 0);
    const iva = form.lineas.reduce((s, l) => s + (parseFloat(l.subtotal) || 0) * (Math.max(parseFloat(l.iva_porc) || 0, 0) / 100), 0);
    return { subtotal: +subtotal.toFixed(2), iva: +iva.toFixed(2), total: +(subtotal + iva).toFixed(2) };
  }, [form.lineas]);
  const resumenCaja = useMemo(() => calcResumenCaja(form.lineas), [form.lineas]);

  // ITER179: detectar productos con corta fecha para banner de alerta
  const cortaFechaLineas = useMemo(() => (
    form.lineas.filter(l => l._vencimiento_corto)
  ), [form.lineas]);
  const diasAlerta = form.lineas.find(l => l._dias_alerta_venc_corto)?._dias_alerta_venc_corto ?? 180;

  // ITER184 (Feb 2026): consolidar múltiples cotizaciones en una sola NP
  const applyConsolidation = async (data) => {
    if (!data?.lineas?.length) {
      toast.warning("La consolidación no devolvió líneas");
      return;
    }
    const lineasHidratadas = await Promise.all(
      data.lineas.map(async (l, idx) => {
        const base = { ...emptySalesLine(), ...l, _lineId: makeLineId(idx) };
        if (!l.producto_comercial_id) return base;
        try {
          const params = new URLSearchParams();
          if (data.cliente_id) params.append("cliente_id", data.cliente_id);
          const qs = params.toString() ? `?${params.toString()}` : "";
          const { data: info } = await api.get(`/sales/producto-info/${l.producto_comercial_id}${qs}`);
          const hydrated = applyProductoInfoToLine(base, info);
          return recalcSubtotal({
            ...hydrated,
            cantidad: l.cantidad,
            precio_unitario: l.precio_unitario,
            descuento_pct: l.descuento_pct ?? 0,
            lote_id: l.lote_id || hydrated.lote_id,
            numero_lote: l.numero_lote || hydrated.numero_lote,
            _unidad_venta: l.unidad_venta || hydrated._unidad_venta,
          });
        } catch (_) {
          return base;
        }
      })
    );
    setForm(f => ({
      ...f,
      cliente_id: data.cliente_id,
      observaciones: data.observaciones_concatenadas || f.observaciones,
      lineas: lineasHidratadas,
    }));
    setCotizIds(data.cotizaciones_ids || []);
    setPrecioDivergenteAviso(!!data.algun_precio_divergente);
  };

  // ITER221: chequeo de stock en otro laboratorio antes de guardar
  const [sugerencias, setSugerencias] = useState([]);
  const [confirmSalir, setConfirmSalir] = useState(false);
  const snapshotRef = useRef(null);
  const esDirty = () => snapshotRef.current !== null && snapshotRef.current !== JSON.stringify(form);

  const reemplazarPorAlternativa = async (idx, alt) => {
    await onProductoChange(idx, alt.producto_comercial_id);
    setSugerencias(prev => prev.filter(s => s.linea_idx !== idx));
    toast.warning(`Producto reemplazado por ${alt.nombre_comercial}. Revise las cantidades de esa línea.`, { duration: 8000 });
  };

  const chequearAlternativas = async () => {
    try {
      const { data } = await api.post("/sales/verificar-alternativas-stock", {
        cliente_id: form.cliente_id || null,
        excluir_pedido_id: editingId || null,
        lineas: form.lineas.filter(l => l.producto_comercial_id).map(l => ({
          producto_comercial_id: l.producto_comercial_id,
          cantidad: Number(l.cantidad) || 0,
          unidad_venta: l._unidad_venta || "unidad",
          unidades_por_caja: l._unidades_por_caja || 1,
          producto_nombre: l.producto_nombre || "",
        })),
      });
      return data?.sugerencias || [];
    } catch (e) { return []; }
  };

  useEffect(() => {
    if (!loading && snapshotRef.current === null) snapshotRef.current = JSON.stringify(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const save = async (opts = {}) => {
    if (!form.cliente_id) { toast.error("Seleccione un cliente"); return; }
    if (form.lineas.length === 0) { toast.error("Agregue al menos un producto"); return; }
    if (form.lineas.some(l => !l.producto_comercial_id)) { toast.error("Hay líneas sin producto"); return; }
    if (!opts.omitirChequeo) {
      setSaving(true);
      const sug = await chequearAlternativas();
      setSaving(false);
      if (sug.length) { setSugerencias(sug); return; }
    }
    // NOTA: En notas de pedido NO se valida precio mínimo ni se exige PIN de
    // gerencia. Esa validación se mantiene solo en facturas electrónicas.
    try {
      setSaving(true);
      const lineas = form.lineas.map((l) => {
        // eslint-disable-next-line no-unused-vars
        const { _lotes_disponibles, _unidad_venta, _unidades_por_caja, _precio_unidad, _precio_caja,
                _pvp_unidad, _pvp_caja, _precio_minimo_unidad, _precio_minimo_caja, _costo_unidad,
                _margen_aplicado, _margen_fuente, _stock_total, _lineId,
                _vencimiento_corto, _dias_hasta_vencimiento, _dias_alerta_venc_corto,
                _auto_split_from, ...rest } = l;
        return {
          ...rest,
          unidad_venta: _unidad_venta || "unidad",
          unidades_por_caja: _unidades_por_caja || null,
        };
      });
      const payload = { ...form, lineas, ...totales };
      if (editingId) {
        await api.put(`/sales/pedidos/${editingId}`, payload);
        toast.success("Nota de pedido actualizada");
      } else {
        const { data: created } = await api.post("/sales/pedidos", payload);
        toast.success(`Nota de pedido ${created?.numero || ""} creada`, {
          duration: 8000,
          action: {
            label: "Compartir PDF",
            onClick: async () => {
              try {
                const r = await shareApiPdf(`/sales/pedidos/${created.id}/pdf`, {
                  filename: `NotaPedido_${created.numero}.pdf`,
                  title: `Nota de Pedido ${created.numero}`,
                  text: `Confirmo su pedido ${created.numero} por $${(created.total || 0).toFixed(2)}. Adjunto el detalle en PDF.`,
                });
                if (r.fallback === "downloaded") toast.message("PDF descargado · Adjúntelo manualmente en WhatsApp");
              } catch (e) {
                toast.error(formatApiError(e));
              }
            },
          },
        });
        // ITER145 (Feb 2026): Ofrecer notificar a Secretaría de Ventas
        setTimeout(() => {
          if (window.confirm(
            `¿Notificar a Secretaría de Ventas (sec.ventas@pharma-stern.com) sobre la nueva Nota de Pedido ${created.numero}?\n\n` +
            `Recibirán un correo con el detalle para que procedan a facturar.`
          )) {
            api.post(`/sales/pedidos/${created.id}/notificar-ventas`)
              .then(({ data }) => toast.success(`Correo enviado a ${(data.destinatarios || []).join(", ")}`))
              .catch((e) => toast.error(formatApiError(e)));
          }
        }, 350);
      }
      snapshotRef.current = null;
      navigate("/pedidos");
    } catch (e) {
      // ITER181 (Feb 2026): auto-recuperación en error de reserva/lote insuficiente.
      // Si el backend rechaza porque un lote específico no tiene stock efectivo,
      // rehidratamos lotes disponibles y disparamos automáticamente el diálogo
      // de auto-split FEFO en la línea afectada.
      const detail = e?.response?.data?.detail || "";
      const isLoteError = /Lote\s+\S+.*(?:ya\s+está\s+reservado|no\s+tiene\s+stock\s+suficiente)/i.test(detail);
      if (isLoteError) {
        // Extraer número de lote del mensaje: "Lote 2023295 de AMLODIPINO ..."
        const m = detail.match(/Lote\s+(\S+)/i);
        const loteNum = m ? m[1] : null;
        if (loteNum) {
          const idx = form.lineas.findIndex(l => (l.numero_lote || "") === loteNum);
          if (idx >= 0) {
            try {
              const params = new URLSearchParams();
              if (form.cliente_id) params.append("cliente_id", form.cliente_id);
              if (editingId) params.append("excluir_pedido_id", editingId);
              const qs = params.toString() ? `?${params.toString()}` : "";
              const { data: info } = await api.get(`/sales/producto-info/${form.lineas[idx].producto_comercial_id}${qs}`);
              setForm(f => {
                const nl = [...f.lineas];
                nl[idx] = { ...nl[idx], _lotes_disponibles: info.lotes_disponibles || [] };
                setTimeout(() => detectFEFOSplit(idx, nl[idx]), 150);
                return { ...f, lineas: nl };
              });
              toast.warning("Este lote ya no tiene stock suficiente. Te propuse distribuir en otros lotes disponibles.");
              return;
            } catch (_) { /* fall-through al toast normal */ }
          }
        }
      }
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const cancelar = () => {
    if (esDirty()) { setConfirmSalir(true); return; }
    navigate("/pedidos");
  };

  if (loading) {
    return <div className="p-10 text-center text-[#5c7368]">Cargando…</div>;
  }

  return (
    <div className="space-y-4 pb-40" data-testid="order-form-page">
      {/* ITER179: Banner de alerta corta fecha */}
      {cortaFechaLineas.length > 0 && (
        <div className="mx-4 md:mx-6 mt-2 border-l-4 border-[#c67c1e] bg-[#fdf3e0] p-3 rounded-r-md flex items-start gap-3" data-testid="alerta-corta-fecha">
          <AlertTriangle className="w-5 h-5 text-[#c67c1e] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#7a4b0b]">
            <b>Atención:</b> {cortaFechaLineas.length} producto(s) usan lote(s) con vencimiento menor a {diasAlerta} días:
            <ul className="mt-1 space-y-0.5">
              {cortaFechaLineas.slice(0, 5).map((l, i) => (
                <li key={i} className="text-xs">
                  · {l.producto_nombre} · Lote {l.numero_lote || "—"} · vence {l.fecha_vencimiento || "—"}
                  {l._dias_hasta_vencimiento != null && <span className="ml-1 font-semibold">({l._dias_hasta_vencimiento} días)</span>}
                </li>
              ))}
              {cortaFechaLineas.length > 5 && <li className="text-xs">... y {cortaFechaLineas.length - 5} más</li>}
            </ul>
          </div>
        </div>
      )}
      {/* Header sticky */}
      <div className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-[#f7f9f8]/95 backdrop-blur border-b border-[#d1dbd6]">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={cancelar} data-testid="order-form-back-btn" className="flex-shrink-0">
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5c7368]">Ventas / Notas de pedido</div>
            <h1 className="text-lg md:text-2xl font-bold tracking-tight text-[#15241d] truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <ClipboardList className="inline mr-2 -mt-0.5" size={20} />
              {editingId ? (
                <>
                  Nota de pedido
                  {form.numero ? (
                    <span className="ml-2 text-[#205c45] font-mono" data-testid="order-form-numero">N° {form.numero}</span>
                  ) : null}
                  {form.estado ? (
                    <span className="ml-2 text-xs uppercase tracking-wider text-[#5c7368] font-semibold align-middle">· {form.estado}</span>
                  ) : null}
                </>
              ) : "Nueva nota de pedido"}
            </h1>
          </div>
          {/* Acciones desktop en header */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" onClick={cancelar} disabled={saving} data-testid="order-form-cancel-btn-desktop">Cancelar</Button>
            <Button onClick={() => save()} disabled={saving} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="order-form-save-btn-desktop">
              <Save size={16} className="mr-2" />
              {saving ? "Guardando…" : (editingId ? "Guardar cambios" : "Guardar")}
            </Button>
          </div>
        </div>
      </div>

      {/* Aviso */}
      <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-2 text-[11px] flex items-start gap-2">
        <AlertTriangle size={14} className="text-[#8a5500] mt-0.5 flex-shrink-0" />
        <span className="text-[#8a5500]">Al guardar se validará stock disponible. Si algún producto no tiene stock se rechazará.</span>
      </div>

      {/* Cabecera Cliente + Fecha */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="flex items-center justify-between">
            <span>Cliente (solo aprobados)</span>
            {form.cliente_id && !editingId && (
              <button
                type="button"
                onClick={() => setConsolidateOpen(true)}
                className="text-[11px] text-[#205c45] hover:underline inline-flex items-center gap-1"
                data-testid="order-consolidate-btn"
                title="Fusionar múltiples cotizaciones pendientes del cliente en esta nota de pedido"
              >
                <Plus size={11} /> Consolidar cotizaciones
              </button>
            )}
          </Label>
          <ClienteCombobox
            value={form.cliente_id}
            onChange={(v) => setForm({ ...form, cliente_id: v })}
            clientes={clientes}
            placeholder="Buscar cliente por razón social, RUC o ciudad…"
            testId="order-client"
          />
          {cotizIds.length > 1 && (
            <div className="mt-1 text-[11px] text-[#205c45] bg-[#e9f2ee] border border-[#b2d0c2] rounded px-2 py-1 inline-flex items-center gap-1" data-testid="order-consolidated-badge">
              <Layers size={11} /> NP consolidando {cotizIds.length} cotizaciones
            </div>
          )}
          {precioDivergenteAviso && (
            <div className="mt-1 text-[11px] text-[#8a5500] bg-[#fef7ec] border border-[#f5c477] rounded px-2 py-1 inline-flex items-center gap-1" data-testid="order-price-diverge-warn">
              <AlertTriangle size={11} /> Precios distintos detectados en cotizaciones origen; revisa cada línea.
            </div>
          )}
        </div>
        <div>
          <Label>Fecha</Label>
          <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="order-form-fecha" />
        </div>
        <div className="md:col-span-2">
          <DireccionEntregaSelect
            cliente={clientes.find(c => c.id === form.cliente_id)}
            value={form.direccion_entrega}
            etiqueta={form.direccion_entrega_etiqueta}
            onChange={({ direccion, etiqueta }) => setForm(f => ({ ...f, direccion_entrega: direccion, direccion_entrega_etiqueta: etiqueta }))}
            testId="order-direccion"
          />
        </div>
      </div>

      {/* VISTA MÓVIL */}
      <div className="space-y-3 md:hidden">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Productos</div>
          <Button size="sm" onClick={addLinea} className="bg-[#205c45] hover:bg-[#1a4a37] h-9" data-testid="order-add-line-btn-mobile">
            <Plus size={14} className="mr-1" />Agregar
          </Button>
        </div>
        {form.lineas.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-[#d1dbd6] py-8 text-center text-sm text-[#8ba397]">
            Sin productos. Toque &quot;Agregar&quot; para empezar.
          </div>
        )}
        {form.lineas.map((l, idx) => (
          <SalesLineCard
            key={l._lineId || `lc-${idx}`}
            line={l}
            idx={idx}
            productos={productos}
            onChange={updateLinea}
            onProductoChange={onProductoChange}
            onLoteChange={onLoteChange}
            onRemove={removeLinea}
            onToggleUnidad={toggleUnidadVenta}
            testPrefix="order"
            showIVA={true}
          />
        ))}
        {form.lineas.length > 0 && (
          <>
            <Button size="sm" onClick={addLinea} variant="outline" className="w-full h-10" data-testid="order-add-line-btn-mobile-bottom">
              <Plus size={14} className="mr-1" />Agregar otro producto
            </Button>
            <div className="rounded-lg border border-[#d1dbd6] bg-[#f7f9f8] p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[#5c7368]">Subtotal</span><span className="tabular-nums">${totales.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#5c7368]">IVA</span><span className="tabular-nums">${totales.iva.toFixed(2)}</span></div>
            <div className="flex justify-between pt-1 border-t border-[#d1dbd6]"><span className="font-bold uppercase">Total</span><span className="text-xl font-bold tabular-nums">${totales.total.toFixed(2)}</span></div>
          </div>
          </>
        )}
      </div>

      {/* VISTA ESCRITORIO */}
      <div className="hidden md:block border border-[#d1dbd6] rounded-md">
        <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b border-[#d1dbd6]">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Líneas</div>
          <Button size="sm" variant="outline" onClick={addLinea} data-testid="order-add-line-btn">
            <Plus size={14} className="mr-1" />Agregar producto
          </Button>
        </div>
        <table className="erp-table" style={{ tableLayout: "fixed", width: "100%" }}>
          <thead>
            <tr>
              <th className="w-8"></th>
              <th style={{ width: "260px" }}>Producto</th>
              <th className="w-40">Cant. + Modo</th>
              <th className="w-28">Precio U.</th>
              <th className="w-20">Desc. %</th>
              <th className="w-24">IVA</th>
              <th className="w-28 text-right">Subtotal</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {form.lineas.map((l, idx) => (
              <SalesLineRow
                key={l._lineId || `l-${idx}`}
                line={l}
                idx={idx}
                productos={productos}
                onChange={updateLinea}
                onProductoChange={onProductoChange}
                onLoteChange={onLoteChange}
                onRemove={removeLinea}
                onToggleUnidad={toggleUnidadVenta}
                testPrefix="order"
                showIVA={true}
              />
            ))}
            {form.lineas.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-[#8ba397] text-sm">Sin líneas. Agregue productos.</td></tr>}
            {form.lineas.length > 0 && (
              <tr>
                <td colSpan={8} className="p-2 bg-[#f7fbf9] border-t border-[#d1dbd6]">
                  <Button size="sm" variant="outline" onClick={addLinea} className="w-full" data-testid="order-add-line-btn-bottom">
                    <Plus size={14} className="mr-1" />Agregar producto
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-[#f7f9f8] border-t border-[#d1dbd6]">
            <tr><td colSpan={6} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-1">Subtotal</td><td colSpan={2} className="text-right tabular-nums px-3 py-1">${totales.subtotal.toFixed(2)}</td></tr>
            <tr><td colSpan={6} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-1">IVA</td><td colSpan={2} className="text-right tabular-nums px-3 py-1">${totales.iva.toFixed(2)}</td></tr>
            <tr><td colSpan={6} className="text-right font-bold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-2">Total</td><td colSpan={2} className="text-right font-bold text-lg tabular-nums px-3 py-2">${totales.total.toFixed(2)}</td></tr>
          </tfoot>
        </table>
      </div>

      <ResumenCajaBar resumen={resumenCaja} />

      <div>
        <Label>Observaciones</Label>
        <Textarea rows={2} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} data-testid="order-form-observaciones" />
      </div>

      {/* Barra fija inferior (mobile) — bottom-[64px] para dejar espacio al badge "Made with Emergent" en preview/dev */}
      <div className="fixed bottom-[64px] left-0 right-0 z-[60] border-t border-[#d1dbd6] bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.08)] md:hidden">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <div className="flex flex-col text-right pr-1 leading-tight">
            <span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Total</span>
            <span className="text-lg font-bold tabular-nums text-[#15241d]">${totales.total.toFixed(2)}</span>
          </div>
          <div className="flex gap-2 flex-1 justify-end">
            <Button variant="outline" className="h-11 flex-1 max-w-[120px]" onClick={cancelar} disabled={saving} data-testid="order-form-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={() => save()} disabled={saving} className="bg-[#205c45] hover:bg-[#1a4a37] h-11 flex-1" data-testid="order-form-save-btn">
              <Save size={16} className="mr-2" />
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </div>
      </div>

      <StockAlternativasDialog
        open={sugerencias.length > 0}
        sugerencias={sugerencias}
        saving={saving}
        permitirGuardarIgual={false}
        nota="Una nota de pedido no se puede registrar sin stock disponible. Reemplace el producto por la alternativa con stock o reduzca la cantidad; si necesita comprarlo, genere la solicitud de compra desde la cotización."
        onReemplazar={reemplazarPorAlternativa}
        onCerrar={() => setSugerencias([])}
      />

      <ConfirmSalirDialog
        open={confirmSalir}
        mensaje="Tiene cambios sin guardar en esta nota de pedido. ¿Desea salir y descartarlos?"
        onConfirmar={() => { setConfirmSalir(false); snapshotRef.current = null; navigate("/pedidos"); }}
        onCancelar={() => setConfirmSalir(false)}
      />

      <AutoSplitFEFODialog
        open={!!splitProposal}
        split={splitProposal?.split}
        productoNombre={splitProposal?.producto_nombre}
        onConfirm={applyProposedSplit}
        onCancel={() => setSplitProposal(null)}
      />

      <ConsolidateOrdersDialog
        open={consolidateOpen}
        onClose={() => setConsolidateOpen(false)}
        clienteId={form.cliente_id}
        excluirId={null}
        kind="cotizacion"
        onConsolidated={applyConsolidation}
      />
    </div>
  );
}
